import { useMutation } from "@tanstack/react-query";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { post } from "api/api-methods";
import { useBookingId } from "hooks/use-booking-id";
import { sendToParent, trackEvent, error as logError } from "logger/logger";
import { useParams } from "react-router-dom";
import { Routes } from "routes";
import { urlBuilder } from "search-params/url-builder";
import { State } from "store/store-types";
import { BookingStatus } from "types/booking-status";
import { PaymentTerminalParameters } from "types/request-parameters";
import { WebBooking } from "types/web-booking";
import { getAvailableWaitListOffer } from "utils/booking";
import { TrackingEventType } from "logger/event-utils";

export const usePaymentTerminal = () => {
    const bookingId = useBookingId();
    const paymentTerminal = async ({
        state,
        webBooking,
        isNoShow,
    }: {
        state: State;
        webBooking?: WebBooking;
        isPrepayment?: boolean;
        isNoShow?: boolean;
    }) => {
        const pathname = location.pathname.replace(Routes.FINALIZE, Routes.PAYMENT_CONFIRMATION);
        const baseUrl = `${location.origin}${pathname}${location.search}`;

        const queryParams: Record<string, string | undefined> = {
            isNoShow: isNoShow ? "true" : undefined,
        };
        const returnUrl = urlBuilder(baseUrl, queryParams);

        const data = new PaymentTerminalParameters(returnUrl, state, webBooking);

        let eventName = "PaymentTerminal";
        const startTime = new Date().getTime();

        try {
            const terminalUrl = await post<never, PaymentTerminalParameters>(
                `WebBooking/WebBookings/${bookingId}/PaymentTerminal`,
                data
            );
            window.location.replace(terminalUrl);

            const endTime = new Date().getTime();
            const loadTime = endTime - startTime;
            trackEvent(eventName, {
                guests: state.guests ?? 0,
                children: state.guestsChildren ?? 0,
                date: state.times?.[0]?.start.toISOString() ?? "",
                isWaitingList: (state.times?.length ?? 0) > 1,
                ms: loadTime,
            });

            sendToParent(state, TrackingEventType.paymentTerminal);

            return terminalUrl;
        } catch (error) {
            logError(error);
            eventName = "PaymentTerminalFailed";

            const endTime = new Date().getTime();
            const loadTime = endTime - startTime;
            trackEvent(eventName, {
                guests: state.guests ?? 0,
                children: state.guestsChildren ?? 0,
                date: state.times?.[0]?.start.toISOString() ?? "",
                isWaitingList: (state.times?.length ?? 0) > 1,
                ms: loadTime,
            });

            sendToParent(state, TrackingEventType.paymentTerminalFailed);

            throw error;
        }
    };

    return useMutation({
        mutationFn: paymentTerminal,
        scope: {
            id: "webbooking-create",
        },
    });
};

export const usePaymentTerminalMyBooking = () => {
    const { data: webBooking } = useWebBooking();
    const { system } = useParams();
    const paymentTerminal = async ({
        state,
        isWaitList,
        isPaymentLink,
    }: {
        state: State;
        isWaitList?: boolean;
        isPaymentLink?: boolean;
    }) => {
        if (!webBooking) {
            throw new Error("WebBooking data is required for payment terminal");
        }

        const bookingId = webBooking.guid;

        const baseUrl = `${document.location.origin}/${system}/${webBooking.unitId}/payment-confirmation`;
        const queryParams: Record<string, string | undefined> = {
            offerStartTime: isWaitList
                ? getAvailableWaitListOffer(webBooking)?.startTime?.format("YYYY-MM-DD[T]HH:mm:ss")
                : undefined,
            paymentLink: isPaymentLink ? "true" : undefined,
        };
        const returnUrl = urlBuilder(baseUrl, queryParams);

        const data = new PaymentTerminalParameters(returnUrl, state, webBooking);

        let eventName = "PaymentTerminal";
        const startTime = new Date().getTime();

        try {
            const terminalUrl = await post<never, PaymentTerminalParameters>(
                `WebBooking/WebBookings/${bookingId}/PaymentTerminal`,
                data
            );
            window.location.replace(terminalUrl);

            const endTime = new Date().getTime();
            const loadTime = endTime - startTime;

            trackEvent(eventName, {
                guests: webBooking.guests,
                children: webBooking.guestsChildren,
                date: webBooking.start.toISOString(),
                isWaitingList: webBooking.status === BookingStatus.WaitList,
                ms: loadTime,
            });

            sendToParent(state, TrackingEventType.paymentTerminal);

            return terminalUrl;
        } catch (error) {
            eventName = "PaymentTerminalFailed";
            logError(error);

            const endTime = new Date().getTime();
            const loadTime = endTime - startTime;

            trackEvent(eventName, {
                guests: webBooking.guests,
                children: webBooking.guestsChildren,
                date: webBooking.start.toISOString(),
                isWaitingList: webBooking.status === BookingStatus.WaitList,
                ms: loadTime,
            });

            sendToParent(state, TrackingEventType.paymentTerminalFailed);

            throw error;
        }
    };

    return useMutation({
        mutationFn: paymentTerminal,
        scope: {
            id: "webbooking-create",
        },
    });
};
